import axios from 'axios';

// Use same-origin in production when REACT_APP_API_URL is unset (Docker serves API + frontend together).
// Set REACT_APP_API_URL only when the API lives on a different host.
export const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies (refresh token)
});

const AUTH_PATHS = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh', '/api/auth/logout'];

function isAuthRequest(url = '') {
  return AUTH_PATHS.some((path) => url.includes(path));
}

function isAccessTokenError(errorMessage = '') {
  return /token|jwt|expired|authorization/i.test(errorMessage) &&
    !/credentials|two-factor|password|username|authentication code/i.test(errorMessage);
}

let refreshPromise = null;

// Request interceptor for adding auth token, environment header, and logging
apiClient.interceptors.request.use(
  (config) => {
    // Add access token to requests if available
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add selected trading environment (paper/live) - always send it, default to 'paper'
    const tradingEnv = localStorage.getItem('tradingEnv') || 'paper';
    config.headers['X-Alpaca-Env'] = tradingEnv;
    
    console.log(`API Request: ${config.method.toUpperCase()} ${config.url} [Env: ${tradingEnv}]`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Never intercept login/register/logout — let those errors reach the UI
    if (!originalRequest || isAuthRequest(originalRequest.url)) {
      return Promise.reject(error);
    }

    // If 401/403 (token expired/invalid) and not already retried, try to refresh token
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      const errorMessage = error.response?.data?.error || '';

      if (isAccessTokenError(errorMessage)) {
        originalRequest._retry = true;

        try {
          if (!refreshPromise) {
            const { authApi } = await import('./authApi');
            refreshPromise = authApi.refreshToken().finally(() => {
              refreshPromise = null;
            });
          }

          const response = await refreshPromise;
          const { accessToken } = response.data;

          localStorage.setItem('accessToken', accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default apiClient;


